import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LogoutGuard } from './core/guards/logout.guard';
import { RoleGuard } from './core/guards/role.guard';
import { AuthLevels, PermissionLevels } from './core/models/user.model';
import { AuthGuardService } from './core/services/auth-guard.service';
import { CheckRoleGuardService } from './core/services/check-role.service';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { EmptyPathRedirectGuard } from './core/services/empty-path-redirect.guard';
import { NoAuthGuardService } from './core/services/no-auth-guard.service';
import { ReserveTaxiPartnerGuardService } from './core/services/reserve-taxi-partner-guard.service';
import { FLEET_TAB_ROUTES } from './features/drivers/views/taxifleet-details/taxifleet-details.routes';

// Exported so the sidebar's menu spec can walk it: every path the layout serves must resolve to a
// menu row that owns it, or sit in that spec's explicit list of pages nothing owns.
export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./core/layout/default-layout.component').then((m) => m.DefaultLayoutComponent),
    canActivate: [AuthGuardService, EmptyPathRedirectGuard],
    children: [
      {
        path: '',
        canActivate: [AuthGuardService, EmptyPathRedirectGuard],
        children: [],
        pathMatch: 'full',
      },

      // booking
      {
        path: 'reserve-taxi',
        loadComponent: () =>
          import('./features/booking/views/reserve-taxi/reserve-taxi-with-keyboard-c.component').then(
            (m) => m.ReserveTaxiWithKeyboardCComponent
          ),
        canActivate: [CheckRoleGuardService, ReserveTaxiPartnerGuardService],
      },

      // rides
      {
        path: 'supervisor',
        loadComponent: () => import('./features/rides/views/supervisor/supervisor.component').then((m) => m.SupervisorComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'supervisor/drt',
        redirectTo: 'drt/supervisor',
        pathMatch: 'full',
      },
      {
        // In-page service selector; defaults to the first DRT service on visit.
        path: 'drt/supervisor',
        loadComponent: () => import('./features/drt/views/supervisor/drt-supervisor.component').then((m) => m.DrtSupervisorComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'anomalies/:state',
        loadComponent: () => import('./features/rides/views/anomalies/anomalies.component').then((m) => m.AnomaliesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'service-anomalies/sharing',
        redirectTo: 'sharing/anomalies',
        pathMatch: 'full',
      },
      {
        path: 'sharing/anomalies',
        loadComponent: () =>
          import('./features/sharing/views/anomalies/sharing-anomalies.component').then((m) => m.SharingAnomaliesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'service-anomalies/tpl',
        redirectTo: 'tpl/anomalies',
        pathMatch: 'full',
      },
      {
        path: 'tpl/anomalies',
        loadComponent: () => import('./features/tpl/views/anomalies/tpl-anomalies.component').then((m) => m.TplAnomaliesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'service-anomalies/parking',
        redirectTo: 'parking/anomalies',
        pathMatch: 'full',
      },
      {
        path: 'parking/anomalies',
        loadComponent: () =>
          import('./features/parking/views/anomalies/parking-anomalies.component').then((m) => m.ParkingAnomaliesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'service-anomalies/train',
        redirectTo: 'train/anomalies',
        pathMatch: 'full',
      },
      {
        path: 'train/anomalies',
        loadComponent: () => import('./features/train/views/anomalies/train-anomalies.component').then((m) => m.TrainAnomaliesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'service-anomalies/drt',
        redirectTo: 'drt/anomalies',
        pathMatch: 'full',
      },
      {
        path: 'drt/anomalies',
        loadComponent: () => import('./features/drt/views/anomalies/drt-anomalies.component').then((m) => m.DrtAnomaliesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'transfers',
        loadComponent: () => import('./features/rides/views/transfers/transfers.component').then((m) => m.TransfersComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'requestslist',
        loadComponent: () => import('./features/rides/views/requests-list/requests-list.component').then((m) => m.RequestsListComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'stop-overs',
        redirectTo: 'parking/archive',
        pathMatch: 'full',
      },
      {
        path: 'parking/archive',
        loadComponent: () => import('./features/parking/views/archive/parking-archive.component').then((m) => m.ParkingArchiveComponent),
      },
      {
        path: 'parking/detail/:id',
        loadComponent: () => import('./features/parking/views/detail/parking-detail.component').then((m) => m.ParkingDetailComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'tpls',
        redirectTo: 'tpl/archive',
        pathMatch: 'full',
      },
      {
        path: 'tpl/archive',
        loadComponent: () => import('./features/tpl/views/archive/tpl-archive.component').then((m) => m.TplArchiveComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'tpl/detail/:id',
        loadComponent: () => import('./features/tpl/views/detail/tpl-detail.component').then((m) => m.TplDetailComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'archives/drt',
        redirectTo: 'drt/archive',
        pathMatch: 'full',
      },
      {
        path: 'drt/archive',
        loadComponent: () => import('./features/drt/views/archive/drt-archive.component').then((m) => m.DrtArchiveComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        // The service days list is a tab of the configuration screen, not a screen of its own; the path
        // it briefly had is kept as a redirect so an already-shared link still lands.
        path: 'drt/shifts',
        redirectTo: 'drt/configuration',
        pathMatch: 'full',
      },
      {
        path: 'drt/configuration',
        loadComponent: () => import('./features/drt/views/config/drt-config.component').then((m) => m.DrtConfigComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        // The service-day editor, routed rather than opened over the list: a day's roster is a
        // screenful of its own, and an edit that survives a refresh has to be somewhere in the URL.
        // `new` before `:serviceDayId`, or it would be read as the id of a day called "new".
        path: 'drt/configuration/service-days/new',
        loadComponent: () => import('./features/drt/views/service-day/drt-service-day.component').then((m) => m.DrtServiceDayComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'drt/configuration/service-days/:serviceDayId',
        loadComponent: () => import('./features/drt/views/service-day/drt-service-day.component').then((m) => m.DrtServiceDayComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'sharing-trips',
        redirectTo: 'sharing/archive',
        pathMatch: 'full',
      },
      {
        path: 'sharing/archive',
        loadComponent: () => import('./features/sharing/views/archive/sharing-archive.component').then((m) => m.SharingArchiveComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'train-tickets',
        redirectTo: 'train/archive',
        pathMatch: 'full',
      },
      {
        path: 'train/archive',
        loadComponent: () => import('./features/train/views/archive/train-archive.component').then((m) => m.TrainArchiveComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'details/:id',
        loadComponent: () =>
          import('./features/rides/views/request-details/request-details.component').then((m) => m.RequestDetailsComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'drt/details/:id',
        loadComponent: () => import('./features/drt/views/detail/drt-detail.component').then((m) => m.DrtDetailComponent),
        canActivate: [CheckRoleGuardService],
      },

      // drivers
      {
        path: 'drivers',
        loadComponent: () => import('./features/drivers/views/list/drivers.component').then((m) => m.DriversComponent),
        canActivate: [CheckRoleGuardService],
      },
      // `drivers/all`, `drivers/tovalidate` and `drivers/validated` were three routes onto the same
      // list; the Stato pill filters it in place now, so old links land on the list itself.
      {
        path: 'drivers/:state',
        redirectTo: 'drivers',
        pathMatch: 'full',
      },
      {
        path: 'driver',
        loadComponent: () => import('./features/drivers/views/add-driver/add-driver.component').then((m) => m.AddDriverComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'driver/:id',
        loadComponent: () =>
          import('./features/drivers/views/driver-details/driver-details.component').then((m) => m.DriverDetailsComponent),
        canActivate: [CheckRoleGuardService],
      },
      // Admin-only list of every fleet on the account — taxi, NCC and Connect alike. It replaces
      // the picker the fleet screen used to carry, which is why that screen also answers to an id
      // now. `fleet` is the same screen without one: the path every non-admin account reaches its
      // own fleet through.
      //
      // Those two paths must stay separate, and the id-less one must NOT be widened to
      // `fleets/:id` for non-admins, however tempting the symmetry looks. The endpoints behind
      // this screen (`PUT /v4/fleets/:id/registry`, `PATCH /v4/fleets/:id/config-shift`) are
      // guarded by `requireAdminAndTaxiCompanyTokens`, which checks the caller's *scope* and never
      // compares the id to the caller's own fleet. An id in the URL for a TAXI_COMPANY account is
      // therefore user-controlled input to an endpoint that would happily overwrite somebody
      // else's fleet. Seeding from the session instead is what makes that unreachable from the UI.
      // If the API ever checks ownership, this can collapse into one route — not before.
      {
        path: 'fleets',
        loadComponent: () => import('./features/drivers/views/fleets/fleets.component').then((m) => m.FleetsComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'fleets/:id',
        loadComponent: () =>
          import('./features/drivers/views/taxifleet-details/taxifleet-details.component').then((m) => m.TaxifleetDetailsComponent),
        canActivate: [CheckRoleGuardService],
        canDeactivate: [unsavedChangesGuard],
        // One per tab, and one per rate schedule: componentless, the page drives its tabs from them.
        children: FLEET_TAB_ROUTES,
      },
      {
        path: 'fleet',
        loadComponent: () =>
          import('./features/drivers/views/taxifleet-details/taxifleet-details.component').then((m) => m.TaxifleetDetailsComponent),
        canActivate: [CheckRoleGuardService],
        canDeactivate: [unsavedChangesGuard],
        // One per tab, and one per rate schedule: componentless, the page drives its tabs from them.
        children: FLEET_TAB_ROUTES,
      },
      {
        path: 'vehicle',
        loadComponent: () => import('./features/drivers/views/add-vehicle/add-vehicle.component').then((m) => m.AddVehicleComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'vehicles',
        loadComponent: () => import('./features/drivers/views/vehicles/vehicles.component').then((m) => m.VehiclesComponent),
        canActivate: [CheckRoleGuardService],
      },

      // users
      {
        path: 'userslist',
        loadComponent: () => import('./features/users/views/users-list/users-list.component').then((m) => m.UsersListComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'user/:id',
        loadComponent: () => import('./features/users/views/user-details/user-details.component').then((m) => m.UserDetailsComponent),
        canActivate: [CheckRoleGuardService],
      },

      // companies
      {
        path: 'companies',
        loadComponent: () => import('./features/companies/views/list/companies.component').then((m) => m.CompaniesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'company/details/:id',
        loadComponent: () =>
          import('./features/companies/views/details/edit-company-form.component').then((m) => m.EditCompanyFormComponent),
        canActivate: [CheckRoleGuardService],
      },

      // config
      {
        path: 'planning-grid',
        loadComponent: () => import('./features/config/views/planning-grid/planning-grid.component').then((m) => m.PlanningGridComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'rate-schedules',
        loadComponent: () =>
          import('./features/config/views/rate-schedules/rate-schedules.component').then((m) => m.RateSchedulesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'areas',
        loadComponent: () => import('./features/config/views/areas/areas.component').then((m) => m.AreasComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'areas/new',
        loadComponent: () => import('./features/config/views/area-detail/area-detail.component').then((m) => m.AreaDetailComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'areas/:id/duplicate',
        loadComponent: () => import('./features/config/views/area-detail/area-detail.component').then((m) => m.AreaDetailComponent),
        data: { duplicate: true },
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'areas/:id',
        loadComponent: () => import('./features/config/views/area-detail/area-detail.component').then((m) => m.AreaDetailComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'dispatching-strategies',
        loadComponent: () =>
          import('./features/config/views/dispatching-strategies/dispatching-strategies.component').then(
            (m) => m.DispatchingStrategiesComponent
          ),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'parkings',
        loadComponent: () => import('./features/parking/config/parkings/parkings.component').then((m) => m.ParkingsComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'addresses',
        loadComponent: () => import('./features/config/views/addresses/addresses.component').then((m) => m.AddressesComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'mobile-whitelist',
        loadComponent: () =>
          import('./features/config/views/mobile-whitelist/mobile-whitelist.component').then((m) => m.MobileWhitelistComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'parkings/:id',
        loadComponent: () =>
          import('./features/parking/config/parking-detail/parking-detail.component').then((m) => m.ParkingDetailComponent),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'disabled-mobility/export',
        loadComponent: () =>
          import('./features/config/views/disabled-mobility-export/disabled-mobility-export.component').then(
            (m) => m.DisabledMobilityExportComponent
          ),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'disabled-mobility/import',
        loadComponent: () =>
          import('./features/config/views/disabled-mobility-import/disabled-mobility-import.component').then(
            (m) => m.DisabledMobilityImportComponent
          ),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'disabled-mobility/edit',
        loadComponent: () =>
          import('./features/config/views/disabled-mobility-edit/disabled-mobility-edit.component').then(
            (m) => m.DisabledMobilityEditComponent
          ),
        canActivate: [CheckRoleGuardService],
      },
      {
        path: 'sumup-config',
        loadComponent: () => import('./features/config/views/sumup-config/sumup-config.component').then((m) => m.SumupConfigComponent),
        canActivate: [CheckRoleGuardService],
      },

      // admin
      {
        path: 'payments',
        loadComponent: () => import('./features/admin/views/payments/wepayments.component').then((m) => m.WepaymentsComponent),
        canActivate: [RoleGuard],
        data: { allowedRoles: [AuthLevels.ADMIN, AuthLevels.TAXI_COMPANY], allowedPermissions: [PermissionLevels.GOD] },
      },
      {
        path: 'referral-program',
        loadComponent: () => import('./features/admin/views/referral/referral.component').then((m) => m.ReferralComponent),
        canActivate: [CheckRoleGuardService],
      },
    ],
  },
  {
    path: 'oauth/sumup-callback',
    children: [],
    loadComponent: () => import('./features/config/views/sumup-callback/sumup-callback.component').then((m) => m.SumupCallbackComponent),
    canActivate: [],
  },
  {
    path: 'login',
    loadComponent: () => import('./features/auth/views/login/login.component').then((m) => m.LoginComponent),
    canActivate: [NoAuthGuardService],
  },
  {
    path: 'logout',
    loadComponent: () => import('./features/auth/views/logout/logout.component').then((m) => m.LogoutComponent),
    canActivate: [LogoutGuard],
  },
  {
    path: '**',
    loadComponent: () => import('./features/auth/views/not-found/not-found.component').then((m) => m.NotFoundComponent),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
